/**
 * AlertRing component.
 *
 * Renders an animated SVG circle representing a single alert lifecycle in the
 * concentric zones visualization. Uses Framer Motion to animate the ring's
 * radius and color based on alert state.
 *
 * Alert states (design spec section 3.2 + 9):
 *   collapsing   - Cyan ring materializes at outer edge (r=600) and collapses
 *                  inward to tier radius over 600ms ease-out.
 *   settled      - Ring rests at tier radius; pulses once on arrival.
 *   turning-away - Ring expands outward (r=700) + fades over 800ms ease-in.
 *   entered      - Color journey cyan -> amber -> red over 600ms;
 *                  triple-pulse (3x 80ms linear) on center hit.
 *
 * prefers-reduced-motion:
 *   All motion is replaced by a static circle at the tier's final radius.
 *   The state color still changes (cyan/green/red) as a functional signal.
 *
 * Plan 03-06 implementation; replaces the no-op stub from plan 03-05.
 */
import { motion } from 'framer-motion';
import { getRingRadius } from './concentric-geometry';
import { tokenValues } from '@/design/tokens';
import type { AlertRingEntry } from './useLiveRingState';

// ---------------------------------------------------------------------------
// Re-export AlertRingEntry type for use in ConcentricZonesViz
// ---------------------------------------------------------------------------
export type { AlertRingEntry };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VIEW_BOX_SIZE = 600;
const CENTER = VIEW_BOX_SIZE / 2;
/** Outer edge beyond viewBox for the initial ring materialize position. */
const OUTER_EDGE = VIEW_BOX_SIZE;
/** Expansion target for turning-away (slightly beyond viewBox). */
const EXPAND_TARGET = VIEW_BOX_SIZE + 100;

/** Easing bezier curves from design tokens. */
const EASE_OUT = [0.16, 1, 0.3, 1] as const;
const EASE_IN = [0.7, 0, 0.84, 0] as const;

/**
 * State colors.
 *
 * `gold` (formerly `cyan`) is the SOC Tactical Gold accent that drives the
 * detecting / approaching ring states. The key name is gold for clarity, but
 * we also export a `cyan` alias so any unrenamed call site still resolves to
 * the new accent value.
 */
const COLOR = {
  gold: tokenValues.colors.accent.gold,
  cyan: tokenValues.colors.accent.gold,
  green: tokenValues.colors.status.ok,
  amber: tokenValues.colors.status.warn,
  red: tokenValues.colors.status.alert,
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  /** Full alert ring state from useLiveRingState. */
  state: AlertRingEntry;
  /**
   * When true, all motion animations are replaced by a static color-coded circle.
   * Functional state colors (cyan/green/red) remain visible.
   */
  reducedMotion: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * AlertRing: animated SVG circle for a single alert lifecycle.
 *
 * Renders inside the ConcentricZonesViz SVG element.
 * Each alert gets its own AlertRing keyed by alertId.
 *
 * @param state - AlertRingEntry from useLiveRingState
 * @param reducedMotion - Whether to skip decorative animations
 */
export function AlertRing({ state, reducedMotion }: Props) {
  const finalRadius = getRingRadius(VIEW_BOX_SIZE, state.tier);

  // Determine the resting color for each state
  const stateColor =
    state.state === 'entered'
      ? COLOR.red
      : state.state === 'turning-away'
        ? COLOR.green
        : COLOR.cyan;

  // ---------------------------------------------------------------------------
  // Reduced motion: static circle with state color, no animation
  // ---------------------------------------------------------------------------

  if (reducedMotion) {
    return (
      <circle
        cx={CENTER}
        cy={CENTER}
        r={finalRadius}
        fill="none"
        stroke={stateColor}
        strokeWidth={3}
        data-alert-state={state.state}
        data-alert-id={state.alertId}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Collapsing: materialize at outer edge, collapse to tier radius (600ms)
  // ---------------------------------------------------------------------------

  if (state.state === 'collapsing' || state.state === 'settled') {
    return (
      <motion.circle
        cx={CENTER}
        cy={CENTER}
        fill="none"
        stroke={COLOR.cyan}
        strokeWidth={3}
        initial={{ r: OUTER_EDGE, opacity: 0 }}
        animate={{
          r: finalRadius,
          opacity: 1,
        }}
        transition={{
          duration: 0.6,
          ease: EASE_OUT,
        }}
        data-alert-state={state.state}
        data-alert-id={state.alertId}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Turning-away: expand outward + fade (800ms ease-in)
  // ---------------------------------------------------------------------------

  if (state.state === 'turning-away') {
    return (
      <motion.circle
        cx={CENTER}
        cy={CENTER}
        fill="none"
        stroke={COLOR.green}
        strokeWidth={3}
        initial={{ r: finalRadius, opacity: 1 }}
        animate={{
          r: EXPAND_TARGET,
          opacity: 0,
        }}
        transition={{
          duration: 0.8,
          ease: EASE_IN,
        }}
        data-alert-state={state.state}
        data-alert-id={state.alertId}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Entered: color journey cyan -> amber -> red + triple-pulse
  // ---------------------------------------------------------------------------

  if (state.state === 'entered') {
    return (
      <motion.g data-alert-state={state.state} data-alert-id={state.alertId}>
        {/* Primary ring: color journey cyan -> amber -> red over 600ms */}
        <motion.circle
          cx={CENTER}
          cy={CENTER}
          r={finalRadius}
          fill="none"
          stroke={COLOR.cyan}
          strokeWidth={3}
          animate={{
            stroke: [COLOR.cyan, COLOR.amber, COLOR.red],
          }}
          transition={{
            duration: 0.6,
            ease: 'linear',
            times: [0, 0.5, 1],
          }}
        />
        {/* Triple-pulse ring: 3x 80ms opacity flash */}
        <motion.circle
          cx={CENTER}
          cy={CENTER}
          r={finalRadius}
          fill="none"
          stroke={COLOR.red}
          strokeWidth={6}
          animate={{
            opacity: [0, 1, 0, 1, 0, 1, 0],
          }}
          transition={{
            duration: 0.48,
            ease: 'linear',
            delay: 0.6,
          }}
        />
      </motion.g>
    );
  }

  // Unrecognized state: render nothing (forward compat)
  return null;
}
